/* Reading a published legal document — STORY-048. */

import {
  type LegalDocumentSlug,
  legalDocumentResponseSchema,
  legalIndexResponseSchema,
} from '@eventuley/contracts';
import { useQuery } from '@tanstack/react-query';
import { request } from '../../../shared/api/client.js';

export const legalDocumentKey = (slug: LegalDocumentSlug) => ['legal', slug] as const;
export const legalIndexKey = ['legal', 'index'] as const;

/**
 * Which documents are published.
 *
 * Signup uses this to choose between promising acceptance and admitting the
 * documents are not ready. It fails CLOSED: any error leaves the list empty and
 * the screen shows the weaker sentence, because claiming acceptance on a failed
 * request would assert consent the API is not recording.
 */
export function useLegalIndex() {
  return useQuery({
    queryKey: legalIndexKey,
    queryFn: () => request('/legal', { schema: legalIndexResponseSchema }),
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

/**
 * One document.
 *
 * Cached for the session. A contract changes when its version changes, and a
 * version change is a deploy — not something that happens while somebody is
 * halfway down the page.
 */
export function useLegalDocument(slug: LegalDocumentSlug | undefined) {
  return useQuery({
    /* Disabled rather than defaulted when the slug is unknown. Building a
     * request path out of an unrecognised URL segment is how a typo becomes a
     * request, and there is nothing useful to ask for. */
    enabled: slug !== undefined,
    queryKey: legalDocumentKey(slug ?? 'terms'),
    queryFn: () => request(`/legal/${slug}`, { schema: legalDocumentResponseSchema }),
    staleTime: Number.POSITIVE_INFINITY,
    /* One retry, not three. If the document is a draft the answer is 404 and
     * retrying cannot change it. */
    retry: 1,
  });
}
